import {
  AutocompleteArrayInput,
  AutocompleteInput,
  ReferenceArrayInput,
  ReferenceInput,
  TextInput,
  required,
  useEditContext,
  useRecordContext,
} from 'react-admin';

import { MAX_NUMBER_OF_ENTITIES } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { getTomorrowsDate } from '@app/utils/dateUtils';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Grid, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { CustomRichText } from '@app/components/CustomRichText';
import { CmsAutocompleteArrayInput } from '@app/components/common/CmsAutocompleteArrayInput/CmsAutocompleteArrayInput';
import { CmsCustomMultipleSelectionInput } from '@app/components/common/CmsCustomMultipleSelectionInput';
import { CustomDateInput } from '@app/components/common/CustomDateInput';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { ASPECT_RATIO_TYPES } from '@app/components/common/ImageSelector/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { LabelNotClickableBoolInput } from '../../../components/LabelNotClickableBoolInput/LabelNotClickableBoolInput';

interface MediaFormProps {
  title: string;
  mediaLogo: File | null | undefined;
  handleChangeMediaLogo(logo: File): void;
}

export const MediaForm = ({ mediaLogo, title, handleChangeMediaLogo }: MediaFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  const { isFetching } = useEditContext();
  const record = useRecordContext();

  if (isFetching) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div>
        <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

        <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
          <Grid item xs={12}>
            <ImageSelector
              aspectRatio={ASPECT_RATIO_TYPES.THUMBNAIL}
              getImageId={(record: GenericObject) => record?.vod_file_base_id as string}
              getImagePath={(record: GenericObject) => (record?.vod_file_base as GenericObject)?.path as string}
              image={mediaLogo}
              isMedia={true}
              handleChangeImage={handleChangeMediaLogo}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              helperText={false}
              variant="outlined"
              label="HLS URL"
              source="vod_url"
              disabled
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              helperText={false}
              variant="outlined"
              label="Image URL"
              disabled
              source="thumbnail_url"
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={TextInput}
              language={language}
              validate={[required()]}
              fullWidth
              label="Title"
              source="title"
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              fullWidth
              label="Transcript"
              source="transcript"
            />
          </Grid>
          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={TextInput}
              language={language}
              fullWidth
              label="Subtitle"
              source="subtitle"
            />
          </Grid>

          <Grid item xs={12}>
            <MultiLingualField
              variant="outlined"
              helperText={false}
              Component={CustomRichText}
              language={language}
              fullWidth
              multiline
              label="Description"
              source="summary"
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              helperText={false}
              variant="outlined"
              label="Duration"
              validate={[required()]}
              source="duration"
              disabled
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <CustomDateInput source="expiry_date" label="Expiry Date" minDate={getTomorrowsDate()} />
          </Grid>
          <Grid item xs={12}>
            <ReferenceInput source="event_series_id" reference="series" perPage={MAX_NUMBER_OF_ENTITIES}>
              <AutocompleteInput
                label="Series"
                variant="outlined"
                helperText={false}
                fullWidth
                optionText={(record) => record.name[DEFAULT_LANGUAGE]}
                //TODO: Add series filter
              />
            </ReferenceInput>
          </Grid>
          <Grid item xs={5}>
            <ReferenceInput source="taxonomy_id" reference="taxonomies" perPage={MAX_NUMBER_OF_ENTITIES}>
              <AutocompleteInput
                label="Primary Topic"
                variant="outlined"
                helperText={false}
                validate={[required()]}
                fullWidth
                optionText={(record) => (record?.category_name ? record?.category_name[DEFAULT_LANGUAGE] : '')}
                filterToQuery={genericfilterToQuery}
              />
            </ReferenceInput>
          </Grid>
          <Grid item xs={7}>
            <ReferenceArrayInput source="secondary_topic_ids" reference="taxonomies" perPage={MAX_NUMBER_OF_ENTITIES}>
              <AutocompleteArrayInput
                variant="outlined"
                helperText={false}
                label="Secondary Topic"
                filterToQuery={genericfilterToQuery}
                fullWidth
                autoHighlight
                optionText={(record) => (record?.category_name ? record?.category_name[DEFAULT_LANGUAGE] : '')}
              />
            </ReferenceArrayInput>
          </Grid>
          <Grid item xs={12}>
            <CmsCustomMultipleSelectionInput
              label={'Companies'}
              optionText={(record: any) => record.name[DEFAULT_LANGUAGE]}
              source={'companies'}
              sourceUrl={'companies'}
              initialSuggestions={record.companies}
              sort={{ field: 'short_name', order: 'ASC' }}
              filterToQuery={genericfilterToQuery}
            />
          </Grid>
          <Grid item xs={12}>
            <CmsCustomMultipleSelectionInput
              label={'Experts'}
              optionText={(record: any) =>
                `${record.first_name[DEFAULT_LANGUAGE]} ${record.last_name[DEFAULT_LANGUAGE]}`
              }
              source={'experts'}
              sourceUrl={'experts'}
              initialSuggestions={record.experts}
              sort={{ field: 'first_name', order: 'ASC' }}
              filterToQuery={genericfilterToQuery}
            />
          </Grid>
          <Grid item xs={12}>
            <AutocompleteArrayInput
              variant="outlined"
              helperText={false}
              label="Hosts"
              source="hosts"
              fullWidth
              choices={[
                { id: 'Nadine Blayney', name: 'Nadine Blayney' },
                { id: 'Andrew Geoghegan', name: 'Andrew Geoghegan' },
                { id: 'Juliette Saly', name: 'Juliette Saly' },
                { id: 'David Koch', name: 'David Koch' },
                { id: 'Kylie Merritt', name: 'Kylie Merritt' },
                { id: 'Anna Napoli', name: 'Anna Napoli' },
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <CmsAutocompleteArrayInput
              sourceUrl="media/keywords"
              source={`keywords.${DEFAULT_LANGUAGE}`}
              label="Keywords"
              language={DEFAULT_LANGUAGE}
            />
          </Grid>

          <Grid item xs={12}>
            <LabelNotClickableBoolInput helperText={false} label="Exclude from Latest" source="skip_video" />
          </Grid>
          <Grid item xs={12}>
            <LabelNotClickableBoolInput
              helperText={false}
              label="Published"
              source="status_type"
              parse={(value: boolean) => {
                if (value === true) {
                  return 'ACTIVE';
                } else {
                  return 'INACTIVE';
                }
              }}
              format={(value: string) => {
                if (value === 'ACTIVE') {
                  return true;
                } else {
                  return false;
                }
              }}
            />
          </Grid>
        </Grid>
      </div>
    </LocalizationProvider>
  );
};
